import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { DocumentUser, InsertDocumentUser, NotificationSettings, InsertNotificationSettings } from "@shared/schema";
import { useNotificationSettings } from "./use-notification-settings";

interface UseDocumentAuthProps {
  documentId: string;
}

interface DocumentAuthState {
  currentUser: DocumentUser | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isModalOpen: boolean;
  users: DocumentUser[];
  isLoadingUsers: boolean;
  notificationSettings: NotificationSettings | null;
  isLoadingNotificationSettings: boolean;
  updateNotificationSettings: (settings: Partial<InsertNotificationSettings>) => Promise<void>;
  login: (user: DocumentUser) => void;
  createUser: (userData: Omit<InsertDocumentUser, 'documentId'>) => Promise<void>;
  clearIdentity: () => void;
  openModal: () => void;
  closeModal: () => void;
  showOnboarding: boolean;
  dismissOnboarding: () => void;
  isFirstTimeUser: boolean;
}

export function useDocumentAuth({ documentId }: UseDocumentAuthProps): DocumentAuthState {
  const [currentUser, setCurrentUser] = useState<DocumentUser | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const queryClient = useQueryClient();

  const {
    settings: notificationSettings,
    isLoading: isLoadingNotificationSettings,
    updateSettings: updateNotificationSettings
  } = useNotificationSettings({ 
    documentId, 
    userId: currentUser?.id || null 
  });

  useEffect(() => {
    if (!documentId) return;

    const storageKey = `document-user-${documentId}`;
    const storedUser = localStorage.getItem(storageKey);
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
        setIsModalOpen(false);
      } catch (error) {
        console.warn('Failed to parse stored user data:', error);
        localStorage.removeItem(storageKey);
        setIsModalOpen(true);
      }
    } else {
      setIsModalOpen(true);
    }

    setIsInitialized(true);
  }, [documentId]);

  const { data: users = [], isLoading: isLoadingUsers } = useQuery<DocumentUser[]>({
    queryKey: ['/api/documents', documentId, 'users'],
    enabled: !!documentId,
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData: Omit<InsertDocumentUser, 'documentId'>): Promise<DocumentUser> => {
      const response = await apiRequest('POST', `/api/documents/${documentId}/users`, userData);
      return response.json();
    },
    onSuccess: async (newUser: DocumentUser) => {
      queryClient.setQueryData(
        ['/api/documents', documentId, 'users'],
        (oldUsers: DocumentUser[] | undefined) => [...(oldUsers || []), newUser]
      );
      
      try {
        await apiRequest('POST', `/api/documents/${documentId}/notifications/settings/${newUser.id}`, {
          emailEnabled: true,
          notificationTypes: ['comment_added']
        });
      } catch (error) {
        console.warn('Failed to create default notification settings:', error);
      }
      
      const onboardingKey = `onboarding-completed-${documentId}`;
      const hasCompletedOnboarding = localStorage.getItem(onboardingKey);

      if (!hasCompletedOnboarding) {
        setIsFirstTimeUser(true);
        setShowOnboarding(true);
      }
      
      login(newUser);
    },
  });

  const login = (user: DocumentUser) => {
    const storageKey = `document-user-${documentId}`;
    localStorage.setItem(storageKey, JSON.stringify(user));
    setCurrentUser(user);
    setIsModalOpen(false);
  };

  const createUser = async (userData: Omit<InsertDocumentUser, 'documentId'>) => {
    await createUserMutation.mutateAsync(userData);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const clearIdentity = () => {
    const storageKey = `document-user-${documentId}`;
    localStorage.removeItem(storageKey);
    setCurrentUser(null);
    setIsModalOpen(true);
  };

  const dismissOnboarding = () => {
    const onboardingKey = `onboarding-completed-${documentId}`;
    localStorage.setItem(onboardingKey, 'true');
    setShowOnboarding(false);
    setIsFirstTimeUser(false);
  };

  return {
    currentUser,
    isAuthenticated: !!currentUser,
    isInitialized,
    isModalOpen,
    users,
    isLoadingUsers,
    notificationSettings,
    isLoadingNotificationSettings,
    updateNotificationSettings,
    login,
    createUser,
    clearIdentity,
    openModal,
    closeModal,
    showOnboarding,
    dismissOnboarding,
    isFirstTimeUser,
  };
}
